import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Crossfade slideshow used on package detail hero.
export const Gallery = ({ images = [], alt = "", auto = true, interval = 4500 }) => {
  const pics = images.filter(Boolean);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = pics.length;

  const go = useCallback((n) => { setPaused(true); setI((c) => (n + count) % count); }, [count]);

  useEffect(() => {
    if (!auto || paused || count <= 1) return;
    const t = setInterval(() => setI((c) => (c + 1) % count), interval);
    return () => clearInterval(t);
  }, [auto, paused, count, interval]);

  if (count === 0) return <div className="absolute inset-0 bg-[#0A221C]" />;

  return (
    <div className="absolute inset-0" data-testid="detail-gallery">
      <AnimatePresence initial={false}>
        <motion.img
          key={i}
          src={pics[i]}
          alt={alt}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      {count > 1 && (
        <>
          <button data-testid="gallery-prev" onClick={() => go(i - 1)} aria-label="Previous photo" className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/25 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/40 transition-colors">
            <ChevronLeft size={22} />
          </button>
          <button data-testid="gallery-next" onClick={() => go(i + 1)} aria-label="Next photo" className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/25 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/40 transition-colors">
            <ChevronRight size={22} />
          </button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {pics.map((_, idx) => (
              <button key={idx} data-testid={`gallery-dot-${idx}`} onClick={() => { setPaused(true); setI(idx); }} aria-label={`Photo ${idx + 1}`} className={`h-2 rounded-full transition-all ${idx === i ? "w-7 bg-[#4ef72d]" : "w-2 bg-white/60"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
